import type { ChangeEvent, FocusEvent, MutableRefObject } from 'react';
import { useCallback } from 'react';
import type { Nullable } from 'corgi-types';

import type { DispatchState } from '../../../../types/React';
import type { UnlockSearch } from '../types';

interface UseHandlersOptions {
  ref: MutableRefObject<HTMLInputElement>;
  placeholder: string;
  setValue: DispatchState<string>;
  setIsEditedByUser: DispatchState<boolean>;
  setFocused: DispatchState<boolean>;
  setSearchClearFocused: DispatchState<boolean>;
  isLocked: boolean;
  unlockSearch: UnlockSearch;
  onBlur: () => void;
}

type InputValueSetter = DispatchState<string>;

const getInputValueChangeHandler =
  (setInputValue: InputValueSetter, setIsEditedByUser: DispatchState<boolean>) =>
  ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    setInputValue(value);
    setIsEditedByUser(true);
  };

const getSearchClearClickHandler =
  (ref: Nullable<MutableRefObject<HTMLInputElement>>, setInputValue: InputValueSetter) => () => {
    setInputValue('');
    ref.current?.focus();
  };

export const getBackButtonClickHandler =
  ({
    clearSuggestions,
    unlockSearchAndClearInput,
  }: Record<'clearSuggestions' | 'unlockSearchAndClearInput', () => void>) =>
  () => {
    unlockSearchAndClearInput();
    clearSuggestions();
  };

export const useHandlers = ({
  ref,
  placeholder,
  setValue,
  setIsEditedByUser,
  setFocused,
  setSearchClearFocused,
  unlockSearch,
  isLocked,
  onBlur,
}: UseHandlersOptions) => {
  const handleValueChange = useCallback(getInputValueChangeHandler(setValue, setIsEditedByUser), [
    setValue,
    setIsEditedByUser,
  ]);
  const handleFocus = () => {
    setFocused(true);
  };
  const handleBlur = (event: FocusEvent<HTMLElement> = null) => {
    // Close the search when moving to the next element
    if (event?.relatedTarget && !event.currentTarget.contains(event.relatedTarget)) {
      onBlur();
      if (isLocked) {
        unlockSearch();
      }
    }
    setFocused(false);
  };
  const clearValue = () => {
    setFocused(false);
    setValue('');
    ref.current.setAttribute('placeholder', placeholder);
  };
  const unlockSearchAndClearInput = useCallback(() => {
    unlockSearch();
    clearValue();
  }, [unlockSearch, setValue]);
  const handleSearchClearClick = useCallback(getSearchClearClickHandler(ref, setValue), [
    ref,
    setValue,
  ]);
  const handleSearchClearFocus = () => {
    setSearchClearFocused(true);
  };
  const handleSearchClearBlur = () => {
    setSearchClearFocused(false);
  };

  return {
    handleValueChange,
    handleFocus,
    handleBlur,
    handleSearchClearClick,
    handleSearchClearFocus,
    handleSearchClearBlur,
    clearValue,
    unlockSearchAndClearInput,
  };
};
